// Consolidated Product Configurator Screens Implementation
// Comprehensive product management for insurance brokers

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { MultiSelect } from 'primereact/multiselect';
import { Checkbox } from 'primereact/checkbox';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { Panel } from 'primereact/panel';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { TreeTable } from 'primereact/treetable';
import { Chips } from 'primereact/chips';
import { Steps } from 'primereact/steps';
import { Timeline } from 'primereact/timeline';
import { ProgressBar } from 'primereact/progressbar';
import { Chart } from 'primereact/chart';
import productConfiguratorMockService from '../../services/mockData/productConfiguratorMockData';

// PC-2: Product Template Management
export const ProductTemplateManager = () => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const toast = useRef(null);
  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    loadTemplates();
    if (id) {
      loadTemplateDetails(id);
    }
  }, [id]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const data = await productConfiguratorMockService.getProductTemplates();
      setTemplates(data);
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load templates' });
    } finally {
      setLoading(false);
    }
  };

  const loadTemplateDetails = async (templateId) => {
    try {
      const template = await productConfiguratorMockService.getProductTemplateById(templateId);
      setSelectedTemplate(template);
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load template details' });
    }
  };

  const saveTemplate = async () => {
    try {
      if (selectedTemplate?.id) {
        await productConfiguratorMockService.updateProductTemplate(selectedTemplate.id, selectedTemplate);
      } else {
        await productConfiguratorMockService.createProductTemplate(selectedTemplate);
      }
      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Product template saved' });
      setShowDialog(false);
      loadTemplates();
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to save template' });
    }
  };

  return (
    <div className="product-template-manager p-3">
      <Toast ref={toast} />
      <Card title="Product Template Configuration">
        {!id ? (
          <div>
            <div className="mb-3 flex justify-content-between">
              <h3>Product Templates</h3>
              <Button label="Create Template" icon="pi pi-plus" onClick={() => {
                setSelectedTemplate({});
                setShowDialog(true);
              }} />
            </div>
            <DataTable value={templates} loading={loading} paginator rows={10}>
              <Column field="templateCode" header="Template Code" sortable />
              <Column field="name" header="Product Name" sortable />
              <Column field="category" header="Category" sortable />
              <Column field="version" header="Version" sortable />
              <Column field="status" header="Status" body={(rowData) =>
                <Tag value={rowData.status} severity={rowData.status === 'Active' ? 'success' : 'warning'} />
              } />
              <Column body={(rowData) => (
                <Button icon="pi pi-pencil" className="p-button-text"
                  onClick={() => navigate(`/product-configurator/template/${rowData.id}`)} />
              )} />
            </DataTable>
          </div>
        ) : (
          <div>
            <Button label="Back" icon="pi pi-arrow-left" className="mb-3 p-button-text"
              onClick={() => navigate('/product-configurator/templates')} />
            {selectedTemplate && (
              <TabView>
                <TabPanel header="Basic Information">
                  <div className="grid p-fluid">
                    <div className="col-6">
                      <div className="field">
                        <label>Template Code</label>
                        <InputText value={selectedTemplate.templateCode}
                          onChange={(e) => setSelectedTemplate({...selectedTemplate, templateCode: e.target.value})} />
                      </div>
                      <div className="field">
                        <label>Product Name</label>
                        <InputText value={selectedTemplate.name}
                          onChange={(e) => setSelectedTemplate({...selectedTemplate, name: e.target.value})} />
                      </div>
                      <div className="field">
                        <label>Category</label>
                        <Dropdown value={selectedTemplate.category}
                          options={['Motor', 'Health', 'Property', 'Travel', 'Marine']}
                          onChange={(e) => setSelectedTemplate({...selectedTemplate, category: e.value})} />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="field">
                        <label>Base Rate (%)</label>
                        <InputNumber value={selectedTemplate.baseRate} mode="decimal" minFractionDigits={2}
                          onChange={(e) => setSelectedTemplate({...selectedTemplate, baseRate: e.value})} />
                      </div>
                      <div className="field">
                        <label>Minimum Premium</label>
                        <InputNumber value={selectedTemplate.minPremium} mode="currency" currency="PHP"
                          onChange={(e) => setSelectedTemplate({...selectedTemplate, minPremium: e.value})} />
                      </div>
                      <div className="field">
                        <label>Commission Rate (%)</label>
                        <InputNumber value={selectedTemplate.commissionRate} suffix="%"
                          onChange={(e) => setSelectedTemplate({...selectedTemplate, commissionRate: e.value})} />
                      </div>
                    </div>
                  </div>
                  <Button label="Save Template" icon="pi pi-save" onClick={saveTemplate} />
                </TabPanel>
                <TabPanel header="Features">
                  <div className="grid">
                    <div className="col-6">
                      <h4>Mandatory Features</h4>
                      <Chips value={selectedTemplate.features?.mandatory} />
                    </div>
                    <div className="col-6">
                      <h4>Optional Features</h4>
                      <Chips value={selectedTemplate.features?.optional} />
                    </div>
                  </div>
                </TabPanel>
                <TabPanel header="Insurers">
                  <MultiSelect value={selectedTemplate.insurers}
                    options={['Malayan Insurance', 'PGA Insurance', 'Charter Ping An', 'AXA Philippines']}
                    display="chip" placeholder="Select Insurers" />
                </TabPanel>
              </TabView>
            )}
          </div>
        )}
      </Card>

      <Dialog header="Create Product Template" visible={showDialog} style={{width: '50vw'}}
        onHide={() => setShowDialog(false)}>
        <div className="p-fluid">
          <div className="field">
            <label>Template Code</label>
            <InputText value={selectedTemplate?.templateCode}
              onChange={(e) => setSelectedTemplate({...selectedTemplate, templateCode: e.target.value})} />
          </div>
          <div className="field">
            <label>Product Name</label>
            <InputText value={selectedTemplate?.name}
              onChange={(e) => setSelectedTemplate({...selectedTemplate, name: e.target.value})} />
          </div>
          <div className="field">
            <label>Description</label>
            <InputTextarea value={selectedTemplate?.description} rows={3}
              onChange={(e) => setSelectedTemplate({...selectedTemplate, description: e.target.value})} />
          </div>
          <Button label="Save" icon="pi pi-check" onClick={saveTemplate} />
        </div>
      </Dialog>
    </div>
  );
};

// PC-3: Coverage Builder
export const CoverageBuilder = () => {
  const [coverages, setCoverages] = useState([]);
  const [selectedCoverage, setSelectedCoverage] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const toast = useRef(null);

  useEffect(() => {
    loadCoverages();
  }, []);

  const loadCoverages = async () => {
    setLoading(true);
    try {
      const data = await productConfiguratorMockService.getCoverageConfigurations();
      setCoverages(data);
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load coverages' });
    } finally {
      setLoading(false);
    }
  };

  const saveCoverage = async () => {
    try {
      await productConfiguratorMockService.createCoverage(selectedCoverage);
      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Coverage saved' });
      setShowDialog(false);
      loadCoverages();
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to save coverage' });
    }
  };

  const typeBodyTemplate = (rowData) => {
    const severity = rowData.type === 'Mandatory' ? 'danger' : 'info';
    return <Tag value={rowData.type} severity={severity} />;
  };

  return (
    <div className="coverage-builder p-3">
      <Toast ref={toast} />
      <Card title="Coverage Configuration Builder">
        <div className="mb-3 flex justify-content-between">
          <h3>Insurance Coverage Management</h3>
          <Button label="Add Coverage" icon="pi pi-plus" onClick={() => {
            setSelectedCoverage({ limits: [], exclusions: [] });
            setShowDialog(true);
          }} />
        </div>

        <DataTable value={coverages} loading={loading} paginator rows={10}>
          <Column field="coverageCode" header="Code" sortable />
          <Column field="coverageName" header="Coverage Name" sortable />
          <Column header="Type" body={typeBodyTemplate} sortable />
          <Column field="deductible" header="Deductible" sortable
            body={(rowData) => `PHP ${rowData.deductible?.toLocaleString()}`} />
          <Column field="premiumImpact" header="Premium Impact" sortable />
          <Column header="Actions" body={(rowData) => (
            <div className="flex gap-2">
              <Button icon="pi pi-pencil" className="p-button-text" />
              <Button icon="pi pi-copy" className="p-button-text" />
              <Button icon="pi pi-trash" className="p-button-text p-button-danger" />
            </div>
          )} />
        </DataTable>
      </Card>

      <Dialog header="Configure Coverage" visible={showDialog} style={{width: '60vw'}}
        onHide={() => setShowDialog(false)}>
        <div className="p-fluid">
          <div className="grid">
            <div className="col-6">
              <div className="field">
                <label>Coverage Code</label>
                <InputText value={selectedCoverage?.coverageCode}
                  onChange={(e) => setSelectedCoverage({...selectedCoverage, coverageCode: e.target.value})} />
              </div>
              <div className="field">
                <label>Coverage Name</label>
                <InputText value={selectedCoverage?.coverageName}
                  onChange={(e) => setSelectedCoverage({...selectedCoverage, coverageName: e.target.value})} />
              </div>
              <div className="field">
                <label>Type</label>
                <Dropdown value={selectedCoverage?.type}
                  options={['Mandatory', 'Optional']}
                  onChange={(e) => setSelectedCoverage({...selectedCoverage, type: e.value})} />
              </div>
            </div>
            <div className="col-6">
              <div className="field">
                <label>Deductible Amount</label>
                <InputNumber value={selectedCoverage?.deductible} mode="currency" currency="PHP"
                  onChange={(e) => setSelectedCoverage({...selectedCoverage, deductible: e.value})} />
              </div>
              <div className="field">
                <label>Waiting Period (days)</label>
                <InputNumber value={selectedCoverage?.waitingPeriod}
                  onChange={(e) => setSelectedCoverage({...selectedCoverage, waitingPeriod: e.value})} />
              </div>
              <div className="field">
                <label>Premium Impact</label>
                <InputText value={selectedCoverage?.premiumImpact}
                  onChange={(e) => setSelectedCoverage({...selectedCoverage, premiumImpact: e.target.value})} />
              </div>
            </div>
          </div>
          <div className="field">
            <label>Description</label>
            <InputTextarea value={selectedCoverage?.description} rows={3}
              onChange={(e) => setSelectedCoverage({...selectedCoverage, description: e.target.value})} />
          </div>
          <div className="field">
            <label>Exclusions</label>
            <Chips value={selectedCoverage?.exclusions}
              onChange={(e) => setSelectedCoverage({...selectedCoverage, exclusions: e.value})} />
          </div>
          <Button label="Save Coverage" icon="pi pi-check" onClick={saveCoverage} />
        </div>
      </Dialog>
    </div>
  );
};

// PC-4: Rating Engine
export const RatingEngine = () => {
  const [ratingFactors, setRatingFactors] = useState([]);
  const [selectedFactor, setSelectedFactor] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const toast = useRef(null);

  useEffect(() => {
    loadRatingFactors();
  }, []);

  const loadRatingFactors = async () => {
    setLoading(true);
    try {
      const data = await productConfiguratorMockService.getRatingFactors();
      setRatingFactors(data);
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load rating factors' });
    } finally {
      setLoading(false);
    }
  };

  const saveFactor = async () => {
    try {
      await productConfiguratorMockService.createRatingFactor(selectedFactor);
      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Rating factor saved' });
      setShowDialog(false);
      loadRatingFactors();
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to save factor' });
    }
  };

  const expandedRowTemplate = (data) => {
    return (
      <div className="p-3">
        <h5>Rating Rules</h5>
        <DataTable value={data.rules}>
          <Column field="condition" header="Condition" />
          <Column field="factor" header="Factor" />
          <Column field="description" header="Description" />
        </DataTable>
      </div>
    );
  };

  return (
    <div className="rating-engine p-3">
      <Toast ref={toast} />
      <Card title="Rating Engine Configuration">
        <div className="mb-3 flex justify-content-between">
          <h3>Rating Factors & Calculation Rules</h3>
          <div className="flex gap-2">
            <Button label="Test Calculator" icon="pi pi-calculator" className="p-button-secondary" />
            <Button label="Add Factor" icon="pi pi-plus" onClick={() => {
              setSelectedFactor({ rules: [] });
              setShowDialog(true);
            }} />
          </div>
        </div>

        <DataTable value={ratingFactors} loading={loading} paginator rows={10}
          expandedRows={[]} rowExpansionTemplate={expandedRowTemplate}>
          <Column expander style={{ width: '3em' }} />
          <Column field="factorCode" header="Factor Code" sortable />
          <Column field="factorName" header="Factor Name" sortable />
          <Column field="type" header="Type" sortable body={(rowData) =>
            <Tag value={rowData.type} />
          } />
          <Column field="status" header="Status" sortable body={(rowData) =>
            <Tag value={rowData.status} severity={rowData.status === 'Active' ? 'success' : 'warning'} />
          } />
          <Column header="Rules Count" body={(rowData) => rowData.rules?.length || 0} />
          <Column header="Actions" body={(rowData) => (
            <div className="flex gap-2">
              <Button icon="pi pi-pencil" className="p-button-text" />
              <Button icon="pi pi-copy" className="p-button-text" />
            </div>
          )} />
        </DataTable>
      </Card>

      <Dialog header="Configure Rating Factor" visible={showDialog} style={{width: '50vw'}}
        onHide={() => setShowDialog(false)}>
        <div className="p-fluid">
          <div className="field">
            <label>Factor Code</label>
            <InputText value={selectedFactor?.factorCode}
              onChange={(e) => setSelectedFactor({...selectedFactor, factorCode: e.target.value})} />
          </div>
          <div className="field">
            <label>Factor Name</label>
            <InputText value={selectedFactor?.factorName}
              onChange={(e) => setSelectedFactor({...selectedFactor, factorName: e.target.value})} />
          </div>
          <div className="field">
            <label>Type</label>
            <Dropdown value={selectedFactor?.type}
              options={['Multiplicative', 'Additive', 'Discount']}
              onChange={(e) => setSelectedFactor({...selectedFactor, type: e.value})} />
          </div>
          <Button label="Save Factor" icon="pi pi-check" onClick={saveFactor} />
        </div>
      </Dialog>
    </div>
  );
};

// PC-5: Underwriting Rules
export const UnderwritingRules = () => {
  const [rules, setRules] = useState([]);
  const [selectedRule, setSelectedRule] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const toast = useRef(null);

  useEffect(() => {
    loadRules();
  }, []);

  const loadRules = async () => {
    setLoading(true);
    try {
      const data = await productConfiguratorMockService.getUnderwritingRules();
      setRules(data);
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load rules' });
    } finally {
      setLoading(false);
    }
  };

  const saveRule = async () => {
    try {
      await productConfiguratorMockService.createUnderwritingRule(selectedRule);
      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Underwriting rule saved' });
      setShowDialog(false);
      loadRules();
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to save rule' });
    }
  };

  const actionBodyTemplate = (rowData) => {
    const severity = rowData.action === 'Auto-Accept' ? 'success' :
                     rowData.action === 'Refer' ? 'warning' : 'danger';
    return <Tag value={rowData.action} severity={severity} />;
  };

  return (
    <div className="underwriting-rules p-3">
      <Toast ref={toast} />
      <Card title="Underwriting Rules Engine">
        <div className="mb-3 flex justify-content-between">
          <h3>Automated Underwriting Rules</h3>
          <Button label="Add Rule" icon="pi pi-plus" onClick={() => {
            setSelectedRule({});
            setShowDialog(true);
          }} />
        </div>

        <Accordion multiple>
          <AccordionTab header={`Acceptance Rules (${rules.filter(r => r.type === 'Acceptance').length})`}>
            <DataTable value={rules.filter(r => r.type === 'Acceptance')} loading={loading}>
              <Column field="ruleCode" header="Rule Code" sortable />
              <Column field="ruleName" header="Rule Name" sortable />
              <Column field="condition" header="Condition" />
              <Column header="Action" body={actionBodyTemplate} />
              <Column field="authority" header="Authority Level" />
              <Column field="status" header="Status" body={(rowData) =>
                <Tag value={rowData.status} severity={rowData.status === 'Active' ? 'success' : 'warning'} />
              } />
            </DataTable>
          </AccordionTab>
          <AccordionTab header={`Validation Rules (${rules.filter(r => r.type === 'Validation').length})`}>
            <DataTable value={rules.filter(r => r.type === 'Validation')} loading={loading}>
              <Column field="ruleCode" header="Rule Code" sortable />
              <Column field="ruleName" header="Rule Name" sortable />
              <Column field="condition" header="Condition" />
              <Column header="Action" body={actionBodyTemplate} />
              <Column field="authority" header="Authority Level" />
            </DataTable>
          </AccordionTab>
          <AccordionTab header={`Loading Rules (${rules.filter(r => r.type === 'Loading').length})`}>
            <DataTable value={rules.filter(r => r.type === 'Loading')} loading={loading}>
              <Column field="ruleCode" header="Rule Code" sortable />
              <Column field="ruleName" header="Rule Name" sortable />
              <Column field="condition" header="Condition" />
              <Column field="message" header="Loading Description" />
              <Column field="authority" header="Authority Level" />
            </DataTable>
          </AccordionTab>
        </Accordion>
      </Card>

      <Dialog header="Configure Underwriting Rule" visible={showDialog} style={{width: '50vw'}}
        onHide={() => setShowDialog(false)}>
        <div className="p-fluid">
          <div className="field">
            <label>Rule Code</label>
            <InputText value={selectedRule?.ruleCode}
              onChange={(e) => setSelectedRule({...selectedRule, ruleCode: e.target.value})} />
          </div>
          <div className="field">
            <label>Rule Name</label>
            <InputText value={selectedRule?.ruleName}
              onChange={(e) => setSelectedRule({...selectedRule, ruleName: e.target.value})} />
          </div>
          <div className="field">
            <label>Type</label>
            <Dropdown value={selectedRule?.type}
              options={['Acceptance', 'Validation', 'Loading']}
              onChange={(e) => setSelectedRule({...selectedRule, type: e.value})} />
          </div>
          <div className="field">
            <label>Condition</label>
            <InputTextarea value={selectedRule?.condition} rows={3}
              onChange={(e) => setSelectedRule({...selectedRule, condition: e.target.value})} />
          </div>
          <div className="field">
            <label>Action</label>
            <Dropdown value={selectedRule?.action}
              options={['Auto-Accept', 'Refer', 'Decline', 'Apply Loading']}
              onChange={(e) => setSelectedRule({...selectedRule, action: e.value})} />
          </div>
          <Button label="Save Rule" icon="pi pi-check" onClick={saveRule} />
        </div>
      </Dialog>
    </div>
  );
};

// PC-6: Approval Workflows
export const ApprovalWorkflows = () => {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(false);
  const toast = useRef(null);

  useEffect(() => {
    loadWorkflows();
  }, []);

  const loadWorkflows = async () => {
    setLoading(true);
    try {
      const data = await productConfiguratorMockService.getApprovalWorkflows();
      setWorkflows(data);
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load workflows' });
    } finally {
      setLoading(false);
    }
  };

  const workflowTemplate = (workflow) => {
    const events = workflow.stages.map(stage => ({
      status: stage.role,
      date: `SLA: ${stage.sla}`,
      icon: 'pi pi-user',
      color: '#9C27B0'
    }));

    return (
      <Timeline value={events} align="left" className="customized-timeline"
        marker={(item) => <span className="flex align-items-center justify-content-center"
          style={{backgroundColor: item.color, color: 'white', borderRadius: '50%', width: '2rem', height: '2rem'}}>
          <i className={item.icon}></i>
        </span>}
        content={(item) => (
          <Card>
            <h5>{item.status}</h5>
            <p>{item.date}</p>
          </Card>
        )} />
    );
  };

  return (
    <div className="approval-workflows p-3">
      <Toast ref={toast} />
      <Card title="Product Approval Workflows">
        <div className="mb-3 flex justify-content-between">
          <h3>Workflow Configuration</h3>
          <Button label="Create Workflow" icon="pi pi-plus" />
        </div>

        {workflows.map(workflow => (
          <Panel key={workflow.id} header={workflow.workflowName} toggleable className="mb-3">
            <div className="grid">
              <div className="col-8">
                {workflowTemplate(workflow)}
              </div>
              <div className="col-4">
                <Card title="Workflow Details">
                  <div className="field">
                    <label>Code:</label>
                    <p>{workflow.workflowCode}</p>
                  </div>
                  <div className="field">
                    <label>Type:</label>
                    <p><Tag value={workflow.type} /></p>
                  </div>
                  <div className="field">
                    <label>Status:</label>
                    <p><Tag value={workflow.status} severity="success" /></p>
                  </div>
                  <div className="field">
                    <label>Triggers:</label>
                    {workflow.triggers.map(trigger => (
                      <Tag key={trigger} value={trigger} className="mr-2 mb-2" />
                    ))}
                  </div>
                </Card>
              </div>
            </div>
          </Panel>
        ))}
      </Card>
    </div>
  );
};

// PC-7: Market Mapping
export const MarketMapping = () => {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(false);
  const toast = useRef(null);

  useEffect(() => {
    loadMappings();
  }, []);

  const loadMappings = async () => {
    setLoading(true);
    try {
      const data = await productConfiguratorMockService.getMarketMapping();
      setMappings(data);
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load market mappings' });
    } finally {
      setLoading(false);
    }
  };

  const progressBodyTemplate = (rowData) => {
    const percentage = (rowData.ytdPremium / rowData.targetPremium) * 100;
    return (
      <div>
        <ProgressBar value={percentage} showValue={false} style={{ height: '20px' }} />
        <small>{percentage.toFixed(1)}% of target</small>
      </div>
    );
  };

  return (
    <div className="market-mapping p-3">
      <Toast ref={toast} />
      <Card title="Product Market Mapping">
        <div className="mb-3 flex justify-content-between">
          <h3>Insurer Product Mapping & Performance</h3>
          <Button label="Map Product" icon="pi pi-plus" />
        </div>

        <DataTable value={mappings} loading={loading} paginator rows={10}>
          <Column field="productId" header="Product" />
          <Column field="insurerName" header="Insurer" sortable />
          <Column field="productCode" header="Insurer Code" />
          <Column field="commissionRate" header="Commission %" sortable
            body={(rowData) => `${rowData.commissionRate}%`} />
          <Column field="overrideRate" header="Override %" sortable
            body={(rowData) => `${rowData.overrideRate}%`} />
          <Column field="targetPremium" header="Target" sortable
            body={(rowData) => `PHP ${(rowData.targetPremium / 1000000).toFixed(1)}M`} />
          <Column header="YTD Performance" body={progressBodyTemplate} />
          <Column field="status" header="Status" body={(rowData) =>
            <Tag value={rowData.status} severity="success" />
          } />
        </DataTable>
      </Card>
    </div>
  );
};

// PC-8: Document Manager
export const DocumentManager = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const toast = useRef(null);

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const data = await productConfiguratorMockService.getDocumentTemplates();
      setDocuments(data);
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load documents' });
    } finally {
      setLoading(false);
    }
  };

  const formatBodyTemplate = (rowData) => {
    const icon = rowData.format === 'PDF' ? 'pi pi-file-pdf' :
                 rowData.format === 'Excel' ? 'pi pi-file-excel' : 'pi pi-file';
    return <i className={icon} style={{ fontSize: '1.5rem' }}></i>;
  };

  return (
    <div className="document-manager p-3">
      <Toast ref={toast} />
      <Card title="Document Template Manager">
        <div className="mb-3 flex justify-content-between">
          <h3>Product Document Templates</h3>
          <Button label="Upload Template" icon="pi pi-upload" />
        </div>

        <DataTable value={documents} loading={loading} paginator rows={10}>
          <Column header="Format" body={formatBodyTemplate} />
          <Column field="documentCode" header="Document Code" sortable />
          <Column field="documentName" header="Document Name" sortable />
          <Column field="type" header="Type" sortable />
          <Column field="stage" header="Stage" sortable />
          <Column field="mandatory" header="Required" body={(rowData) =>
            rowData.mandatory ? <i className="pi pi-check text-green-500"></i> : <i className="pi pi-times text-red-500"></i>
          } />
          <Column header="Actions" body={(rowData) => (
            <div className="flex gap-2">
              <Button icon="pi pi-download" className="p-button-text" tooltip="Download" />
              <Button icon="pi pi-pencil" className="p-button-text" tooltip="Edit" />
              <Button icon="pi pi-eye" className="p-button-text" tooltip="Preview" />
            </div>
          )} />
        </DataTable>
      </Card>
    </div>
  );
};

// PC-9: Product Analytics
export const ProductAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const toast = useRef(null);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const data = await productConfiguratorMockService.getProductAnalytics();
      setAnalytics(data);
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load analytics' });
    } finally {
      setLoading(false);
    }
  };

  const performanceChart = {
    labels: analytics?.performanceTrend?.map(t => t.month) || [],
    datasets: [
      {
        label: 'Premium (PHP millions)',
        data: analytics?.performanceTrend?.map(t => t.premium / 1000000) || [],
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        borderColor: 'rgb(54, 162, 235)',
        tension: 0.4
      }
    ]
  };

  const categoryChart = {
    labels: Object.keys(analytics?.categoryBreakdown || {}),
    datasets: [{
      data: Object.values(analytics?.categoryBreakdown || {}).map(c => c.premium / 1000000),
      backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF']
    }]
  };

  return (
    <div className="product-analytics p-3">
      <Toast ref={toast} />
      <Card title="Product Performance Analytics">
        <div className="grid">
          <div className="col-8">
            <Card title="Premium Trend">
              <Chart type="line" data={performanceChart} />
            </Card>
          </div>
          <div className="col-4">
            <Card title="Category Distribution">
              <Chart type="pie" data={categoryChart} />
            </Card>
          </div>
        </div>

        <Card title="Top Products Performance" className="mt-3">
          <DataTable value={analytics?.topProducts || []}>
            <Column field="productName" header="Product" />
            <Column field="totalPolicies" header="Policies" sortable
              body={(rowData) => rowData.totalPolicies.toLocaleString()} />
            <Column field="totalPremium" header="Premium" sortable
              body={(rowData) => `PHP ${(rowData.totalPremium / 1000000).toFixed(1)}M`} />
            <Column field="avgPremium" header="Avg Premium" sortable
              body={(rowData) => `PHP ${rowData.avgPremium.toLocaleString()}`} />
            <Column field="lossRatio" header="Loss Ratio" sortable
              body={(rowData) => (
                <div>
                  <ProgressBar value={rowData.lossRatio} showValue={false} />
                  <small>{rowData.lossRatio}%</small>
                </div>
              )} />
            <Column field="profitMargin" header="Profit Margin" sortable
              body={(rowData) => <Tag value={`${rowData.profitMargin}%`} severity="success" />} />
          </DataTable>
        </Card>
      </Card>
    </div>
  );
};